import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Play, Star, CheckCircle, Clock, Users, Award, TrendingUp, Sparkles, BookOpen, Zap, Target, ChevronDown, ChevronUp, Shield, ArrowRight } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CountdownTimer from '@/components/CountdownTimer';
import { siteVideoService } from '@/services/siteVideoService';
import { trackMicroConversion } from '@/services/analytics';
import { setEnrollmentProduct } from '@/redux/actions/enrollment';
import { useProductPricing, formatPrice } from '@/hooks/useFeaturedPricing';
import { metaPixel } from '@/services/metaPixel';

export default function PromptEngineeringMastery() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [expandedModule, setExpandedModule] = useState<number | null>(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  // Fetch dynamic pricing from API - SINGLE SOURCE OF TRUTH
  const {
    loading: pricingLoading,
    displayPrice,
    originalPrice,
    isSaleActive,
    savingsAmount,
    savingsPercentage
  } = useProductPricing('prompt-engineering-mastery', 'course');

  // Handle enrollment click
  const handleEnrollClick = () => {
    // Set enrollment product using slug (stable identifier)
    dispatch(setEnrollmentProduct('prompt-engineering-mastery', 'course') as any);
    navigate('/enrollment');
  };

  // Fetch PEM hero video
  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    const fetchVideo = async () => {
      try {
        const videoData = await siteVideoService.getSiteVideo('pem_hero');
        if (videoData.playback_url) {
          setVideoUrl(videoData.playback_url);
        }
      } catch (error) {
      }
    };

    fetchVideo();
  }, []);

  // Track page view on mount
  useEffect(() => {
    trackMicroConversion.pageView('Prompt Engineering Mastery');
  }, []);

  // Sticky CTA scroll handler and scroll depth tracking
  useEffect(() => {
    const scrollTracked = {
      depth25: false,
      depth50: false,
      depth75: false,
      depth90: false
    };

    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);

      // Track scroll depth milestones
      const scrollPercent =
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

      if (scrollPercent > 25 && !scrollTracked.depth25) {
        trackMicroConversion.scrollDepth(25);
        scrollTracked.depth25 = true;
      }
      if (scrollPercent > 50 && !scrollTracked.depth50) {
        trackMicroConversion.scrollDepth(50);
        scrollTracked.depth50 = true;
      }
      if (scrollPercent > 75 && !scrollTracked.depth75) {
        trackMicroConversion.scrollDepth(75);
        scrollTracked.depth75 = true;
      }
      if (scrollPercent > 90 && !scrollTracked.depth90) {
        trackMicroConversion.scrollDepth(90);
        scrollTracked.depth90 = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set promotion end date (7 days from now - adjust as needed)
  const promoEndDate = new Date();
  promoEndDate.setDate(promoEndDate.getDate() + 7);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Prompt Engineering Mastery - AI Mastery Hub</title>
        <meta name="description" content="Master prompt engineering and unlock expert-level AI interactions. Transform from AI novice to prompt master." />
      </Helmet>

      <Navigation minimal={true} />

      {/* Hero Section with Video */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-violet-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
              <span className="text-purple-400 font-semibold">🔥 BESTSELLER</span>
            </div>

            {/* Instructor Photo - Human Element */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-violet-400 p-1">
                  <img
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces"
                    alt="Course Instructor"
                    width={96}
                    height={96}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-green-500 w-8 h-8 rounded-full border-4 border-black flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Become a Prompt Engineering Master in Days
            </h1>
            <p className="text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Stop wasting hours on mediocre AI responses and start producing expert-level results
            </p>

            {/* Social Proof */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-black"></div>
                  ))}
                </div>
                <span className="text-sm text-gray-400">Growing enrollment</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
                <span className="text-sm text-gray-400 ml-2">7 hours expert training</span>
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="max-w-5xl mx-auto mb-12">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-violet-600 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-4 border border-white/20">
                <div className="relative aspect-video bg-gradient-to-br from-purple-900/50 to-violet-900/50 rounded-2xl overflow-hidden">
                  {videoUrl ? (
                    <video
                      src={videoUrl}
                      controls
                      onPlay={() => trackMicroConversion.videoPlay('hero')}
                      className="w-full h-full object-cover"
                      poster="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=675&fit=crop"
                      aria-label="Prompt Engineering Mastery course preview video"
                    />
                  ) : (
                    <>
                      <img
                        src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=675&fit=crop"
                        alt="Course Preview"
                        width={1200}
                        height={675}
                        className="w-full h-full object-cover"
                      />
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-all cursor-pointer group"
                        role="button"
                        aria-label="Play course preview video"
                        tabIndex={0}
                      >
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                          <Play className="w-8 h-8 text-black fill-black ml-1" />
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                        <span className="text-sm font-semibold">3:24 Preview</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => {
                trackMicroConversion.ctaClick('hero');
                handleEnrollClick();
              }}
              className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 px-12 py-5 rounded-xl font-bold text-xl flex items-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group"
              aria-label={`Enroll in Prompt Engineering Mastery course for ${formatPrice(displayPrice)}`}
            >
              <span>Enroll Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            {isSaleActive && savingsAmount > 0 && (
              <div className="text-center">
                <div className="text-2xl text-gray-300 line-through font-medium decoration-2">{formatPrice(originalPrice)}</div>
                <div className="text-sm text-purple-400">Save {savingsPercentage}% Today</div>
              </div>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Lifetime Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Expert Support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Certificate Included</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/10 via-black to-violet-900/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Problem */}
            <div>
              <h2 className="text-4xl font-bold mb-6 text-red-400">The Problem</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You're wasting hours getting mediocre results from AI</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Your prompts are vague, resulting in generic, unusable outputs</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You don't know the frameworks that pros use to get 10x results</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Your competitors are mastering AI while you're left behind</p>
                </div>
              </div>
            </div>

            {/* Solution */}
            <div>
              <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">The Solution</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Master 7 core prompting skills that professionals use daily</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Get proven frameworks (CLEAR, Chain-of-Thought, Few-Shot)</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Build your personal prompt library worth $1000s</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Transform from AI novice to prompt engineering master</p>
                </div>
              </div>
            </div>
          </div>

          {/* Transformation Statement */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-purple-600/20 to-violet-600/20 border border-purple-500/30 rounded-2xl px-8 py-6">
              <p className="text-xl text-gray-300">
                <span className="text-purple-400 font-bold">Join over 2,000 professionals</span> who increased productivity by <span className="text-green-400 font-bold">10×</span> using these prompt strategies
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">What You'll Master</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              8 comprehensive modules covering everything from basics to advanced techniques
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: 'AI Fundamentals', desc: 'Understand how LLMs work and when to use which tool' },
              { icon: Target, title: 'Perfect Prompts', desc: 'Master the anatomy of effective prompts with examples' },
              { icon: BookOpen, title: 'Core Skills', desc: 'Learn 7 essential prompting techniques with practice' },
              { icon: Zap, title: 'Advanced Frameworks', desc: 'CLEAR method, Chain-of-Thought, Few-Shot prompting' },
              { icon: TrendingUp, title: 'Real Applications', desc: 'Content, research, communication, and more' },
              { icon: Award, title: 'Prompt Library', desc: 'Build your personal collection of winning prompts' },
              { icon: Users, title: 'Capstone Project', desc: 'Create 5 custom prompts for your specific needs' },
              { icon: Clock, title: 'Lifetime Updates', desc: 'Get new content as AI tools evolve' }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all group">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Preview */}
      <section className="py-24 px-6 bg-gradient-to-br from-purple-900/10 via-black to-violet-900/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Course Curriculum</h2>
            <p className="text-xl text-gray-400">7 hours of expert training • 8 modules • 22 lessons</p>
          </div>

          <div className="space-y-4">
            {[
              {
                module: 1,
                title: 'AI Fundamentals & First Prompts',
                duration: '20 min',
                lessons: 2,
                lessonTitles: [
                  'What Are LLMs and How Do They Work?',
                  'Your First Effective Prompt: Hands-On Exercise'
                ]
              },
              {
                module: 2,
                title: 'Foundations & Context',
                duration: '45 min',
                lessons: 3,
                lessonTitles: [
                  'The Anatomy of a Perfect Prompt',
                  'Understanding Context Windows and Token Limits',
                  'Setting the Right Tone and Voice'
                ]
              },
              {
                module: 3,
                title: 'Tools & Setup',
                duration: '40 min',
                lessons: 3,
                lessonTitles: [
                  'Choosing the Right AI Tool for Your Task',
                  'ChatGPT vs Claude vs Gemini: When to Use Which',
                  'Setting Up Your Prompt Engineering Workspace'
                ]
              },
              {
                module: 4,
                title: 'Core Prompting Skills',
                duration: '90 min',
                lessons: 3,
                lessonTitles: [
                  'Clarity: Writing Crystal-Clear Instructions',
                  'Specificity: Getting Exactly What You Need',
                  'Few-Shot Learning: Teaching AI by Example'
                ]
              },
              {
                module: 5,
                title: 'Frameworks & Templates',
                duration: '35 min',
                lessons: 3,
                lessonTitles: [
                  'The CLEAR Framework for Perfect Prompts',
                  'Chain-of-Thought Prompting for Complex Tasks',
                  'Building Your Personal Prompt Library'
                ]
              },
              {
                module: 6,
                title: 'Real-World Applications',
                duration: '50 min',
                lessons: 3,
                lessonTitles: [
                  'Content Creation: Blog Posts, Social Media, Emails',
                  'Research & Analysis: Data Extraction and Insights',
                  'Communication: Professional Writing and Editing'
                ]
              },
              {
                module: 7,
                title: 'Capstone Project',
                duration: '35 min',
                lessons: 4,
                lessonTitles: [
                  'Identifying Your Use Cases',
                  'Creating Custom Prompts for Your Needs',
                  'Testing and Refining Your Prompts',
                  'Sharing Your Success: Show and Tell'
                ]
              },
              {
                module: 8,
                title: 'Advanced Techniques & Support',
                duration: '9 min',
                lessons: 2,
                lessonTitles: [
                  'Advanced Prompt Engineering Strategies',
                  'Staying Updated: The Future of AI'
                ]
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-purple-500/30 transition-all">
                <button
                  onClick={() => {
                    setExpandedModule(expandedModule === idx ? null : idx);
                    if (expandedModule !== idx) {
                      trackMicroConversion.moduleClick(item.module, item.title, 'Prompt Engineering Mastery');
                    }
                  }}
                  className="w-full p-6 text-left"
                  aria-expanded={expandedModule === idx}
                  aria-label={`Toggle module ${item.module}: ${item.title}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                        {item.module}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-white hover:text-purple-400 transition-colors">{item.title}</h3>
                        <p className="text-sm text-gray-400">{item.lessons} lessons • {item.duration}</p>
                      </div>
                    </div>
                    {expandedModule === idx ? (
                      <ChevronUp className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 hover:text-purple-400 transition-colors flex-shrink-0" />
                    )}
                  </div>
                </button>
                {expandedModule === idx && (
                  <div className="px-6 pb-6 pt-2">
                    <div className="ml-16 space-y-3 border-l-2 border-purple-500/30 pl-6">
                      {item.lessonTitles.map((lesson, lessonIdx) => (
                        <div key={lessonIdx} className="flex items-start gap-3 text-gray-300">
                          <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm leading-relaxed">{lesson}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Student Success Stories</h2>
            <p className="text-xl text-gray-400">See how prompt engineering mastery transformed their careers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Chen',
                role: 'Marketing Manager',
                image: 'from-pink-400 to-purple-400',
                text: 'This course 10x my productivity. I now create a week\'s worth of content in just 2 hours using the frameworks taught here.'
              },
              {
                name: 'Michael Rodriguez',
                role: 'Freelance Consultant',
                image: 'from-blue-400 to-cyan-400',
                text: 'The prompt library alone is worth 10x the course price. I\'ve landed 3 new clients just by showing them what I can do with AI.'
              },
              {
                name: 'Emily Watson',
                role: 'Product Manager',
                image: 'from-green-400 to-emerald-400',
                text: 'I was skeptical at first, but the structured approach to prompting changed everything. Now my entire team uses these techniques.'
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.image}`}></div>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-purple-900/20 via-black to-violet-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 to-violet-600 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl border-2 border-white/30 p-12">
              <div className="text-center mb-8">
                <div className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600/30 to-violet-600/30 border border-purple-500/50 rounded-full mb-4">
                  <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                    ⏰ LIMITED TIME OFFER
                  </span>
                </div>
                <h2 className="text-5xl font-bold mb-4">Start Mastering Prompts Today</h2>
                <p className="text-xl text-gray-300">Join students who've transformed their AI game</p>
              </div>

              {/* Countdown Timer */}
              <CountdownTimer targetDate={promoEndDate} />

              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="text-center">
                  <div className="text-6xl font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent mb-2">
                    {pricingLoading ? '...' : formatPrice(displayPrice)}
                  </div>
                  {isSaleActive && savingsAmount > 0 && (
                    <>
                      <div className="text-3xl text-gray-300 line-through font-semibold decoration-2 mb-2">{formatPrice(originalPrice)}</div>
                      <div className="text-lg text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">7 hours of expert training</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">12+ proven prompt templates</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">Lifetime access & updates</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">Certificate of completion</span>
                </div>
              </div>

              <button
                onClick={() => {
                  trackMicroConversion.ctaClick('pricing');
                  handleEnrollClick();
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 py-6 rounded-xl font-bold text-2xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group mb-6"
                aria-label={`Enroll in Prompt Engineering Mastery course for ${formatPrice(displayPrice)}`}
              >
                <span>Enroll Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm">Secure Payment • Instant Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Do I need any prior AI experience?',
                a: 'No! This course is designed for complete beginners. We start from the basics and progressively build your skills.'
              },
              {
                q: 'How long do I have access to the course?',
                a: 'Lifetime! Once you enroll, you have unlimited access forever, including all future updates.'
              },
              {
                q: 'What support is available?',
                a: 'You get access to our dedicated support team to help you get the most out of the course. Contact us anytime with questions.'
              },
              {
                q: 'How is this different from free YouTube tutorials?',
                a: 'This is a structured, comprehensive system with proven frameworks, templates, and real-world applications. You get a complete roadmap, not scattered tips.'
              },
              {
                q: 'Will this work with ChatGPT, Claude, and other AI tools?',
                a: 'Yes! The techniques work with all major AI tools including ChatGPT, Claude, Gemini, and more.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-purple-400">{faq.q}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* Sticky Mobile CTA */}
      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden z-50 bg-gradient-to-r from-purple-600 to-violet-600 p-4 shadow-2xl border-t border-white/20 animate-slide-up">
          <button
            onClick={() => {
              trackMicroConversion.ctaClick('Sticky Mobile CTA - Enroll');
              handleEnrollClick();
            }}
            className="w-full bg-white text-purple-600 hover:bg-gray-100 py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <span>Enroll Now{isSaleActive && savingsPercentage > 0 ? ` - Save ${savingsPercentage}%` : ''}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}
